import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import debounce from 'lodash/debounce';

import {
    isCreateMode,
    isConsultMode,
    isUpdateMode,
    isNoneMode,
    isBackgroundCreateMode,
  } from '../../../../utils/store/store-utils';

  import { getTipoModeloLabelByValue } from  '../../../../shared-components/select-tipo-modelo/QCXFinalSelectTipoModeloAutocomplete';
import QCXRegistrationFormPageTemplate from "../../../../templates/registration-form-page/QCXRegistrationFormPageTemplate";
import QCXTarifaForm from "../../../../components/tarifa/QCXTarifaForm";
import { 
    selectMode,
    selectTarifa,
    setModel,
    selectBackgroundMode,
    changeToConsultMode,
    changeToCreateMode,
    changeToUpdateMode,
    resetModel,
    loading,
    setResponse,
    updateOnList,
    fetchByIdAsync,
    failure,
    setError,
    addToList,
    success,
} from "../../../../features/tarifa/tarifaSlice";
import {
 register,
 save,
} from "../../../../features/tarifa/tarifaAPI"
import { getStringOrUndefined, parseToBoolean } from '../../../../utils/general/parse-utils';

export default function TarifaRegistrationPage({ authInfo = {} }) {

    const history = useHistory();

    const dispatch = useDispatch();

    const { t } = useTranslation();

    const { id } = useParams();

    const mode = useSelector(selectMode);

    const tarifa = useSelector(selectTarifa);

    const backgroundMode = useSelector(selectBackgroundMode);

    const isNone = useMemo(() => (
        isNoneMode(mode)
    ), [mode]);
    
    const isCreate = useMemo(() => (
          isCreateMode(mode)
    ), [mode]);
      
    const isConsult = useMemo(() => (
        isConsultMode(mode)
    ), [mode]);
    
    const isUpdate = useMemo(() => (
      isUpdateMode(mode)
    ), [mode]);
    
    const actionName = useMemo(() => {
    if (isCreate || isNone) {
        return t('com.muralis.qcx.acoes.nova');
    }
    if (isConsult) {
        return t('com.muralis.qcx.acoes.visualizar');
    }
    return t('com.muralis.qcx.acoes.alterar');
    }, [isNone, isCreate, isConsult]);


    const breadcrumbs = useMemo(() => ([
        {
            link: {
                to: '/',
                name: t('com.muralis.qcx.inicio'),
            },
            },
            {
            link: {
                to: t('com.muralis.qcx.url.moduloAgenciamento'),
                name: t('com.muralis.qcx.agenciamento.breadcrumbs.importacao'),
            },
            },
            {
            text: {
                name: t('com.muralis.qcx.agenciamento.tarifa'),
            },
            default: true,
            },
    {
        text: {
        name: actionName,
        },
    },
    ]), [actionName]);

    const pageTitle = useMemo(() => 
    (
        isNone || isCreate
        ? t('com.muralis.qcx.aereo.novo_HAWB')
        : t('com.muralis.qcx.despesa.grupoDespesaReceitaExistente', { ID: tarifa?.id })
    )
    , [
    isNone,
    isCreate,
    tarifa,
    ]);

    const unnormalize = useCallback( (normalizedData) => {
        const unnormalizedData = {
            id: normalizedData?.id ?? null,
            code: normalizedData?.code ?? '',
            description: normalizedData?.description ?? '',
            active : normalizedData?.active ?? true,
            insertionDate : normalizedData?.insertionDate ?? new Date(),
        };
      
        return unnormalizedData;    
    });

    const normalize = useCallback((normalizedData) => {

        const unnormalizedData = {
            id: normalizedData?.id ?? null,
            code: normalizedData?.code ?? '',
            description: getStringOrUndefined(normalizedData?.description) ?? '',
            active : normalizedData?.active ?? true,
            insertionDate : normalizedData?.insertionDate ?? null,
          };
    
        return unnormalizedData;
      }, [
        getStringOrUndefined,
        parseToBoolean,
    ]);

    const model = useMemo(() => unnormalize(tarifa), 
    [
        isCreate,
        tarifa,
        unnormalize,
    ]);
    
    const handleChangeToCreate = () => {
        dispatch(changeToCreateMode());
    };

    const handleChangeToConsult = () => {
        dispatch(changeToConsultMode());
    };
    
    const handleChangeToUpdate = () => {
        dispatch(changeToUpdateMode());
    };

    const handleCancelUpdate = useCallback( () => {

    });

    const create = async (data) => {
        const executeDebounced = debounce(async () => {
          const tipoModelo = getTipoModeloLabelByValue(data?.tipo);
    
          try {
            const response = await register(data);
    
            if (response?.status === 201) {
              dispatch(resetModel());
    
              const created = response?.data;
    
              handleDispatchSetModel(created);
              dispatch(addToList({ data: created }));
    
              const handleResultWithDebounce = debounce(() => {
                history.push(t('com.muralis.qcx.url.modulosFinanceirosGrupoDespesaReceita'));
    
                dispatch(success());
                dispatch(setResponse({
                  status: response.status,
                  data: created,
                  message: t('com.muralis.qcx.mensagem.despesaReceitaCadastrada', { tipoModelo, descricao: data?.description }),
                }));
              }, 500);
    
              handleResultWithDebounce();
            }
          } catch (error) {
            const defaultErrorMessage = t('com.muralis.qcx.erro.erroCadastrarDespesaReceita', { tipoModelo, descricao: data?.description });
            const errorMessage = error?.response?.data?.message
              ? `${defaultErrorMessage}: ${error?.response?.data?.message}.`
              : `${defaultErrorMessage}.`;
    
            dispatch(failure());
            dispatch(setError({
              message: errorMessage,
            }));
          }
        }, 500);
    
        dispatch(loading());
        executeDebounced();
      };

    const update = async (data) => {
        const executeDebounced = debounce(async () => {
          const tipoModelo = getTipoModeloLabelByValue(data?.tipo);
    
          try {
            const response = await save(data);
    
            if (response?.status === 200) {
              const handleResultWithDebounce = debounce(() => {
                handleChangeToConsult();
                dispatch(success());
    
                const saved = response?.data;
    
                dispatch(setResponse({
                  status: response.status,
                  data: saved,
                  message: t('com.muralis.qcx.mensagem.despesaReceitaSalva', { tipoModelo, descricao: saved?.description }),
                }));
    
                dispatch(setModel(saved));
                dispatch(updateOnList({ data: saved }));
                dispatch(fetchByIdAsync(saved?.id));
              }, 500);
    
              handleResultWithDebounce();
              handleChangeToConsult();
            }
          } catch (error) {
            const defaultErrorMessage = t('com.muralis.qcx.erro.erroSalvarDadosDespesaReceita', { tipoModelo, descricao: data?.description });
            const errorMessage = error?.response?.data?.message
              ? `${defaultErrorMessage}: ${error?.response?.data?.message}.`
              : `${defaultErrorMessage}.`;
    
            dispatch(failure());
            dispatch(setError({
              message: errorMessage,
            }));
          }
        }, 500);
    
        dispatch(loading());
        executeDebounced();
      };

    const handleSubmit = async (data) => {
        const normalizedData = normalize(data);
    
        if (isUpdate) {
          await update(normalizedData);
        } else {
          await create(normalizedData);
        }
      };

    const handleDispatchSetModel = useCallback((data) => {

        const normalizedData = normalize(data);
    
        dispatch(
          setModel(normalizedData)
        );
      }, [normalize]);

    return (
       <QCXRegistrationFormPageTemplate
             pageTitle={pageTitle}
             breadcrumbs={breadcrumbs}
             isCreate={isCreate}
             isConsult={isConsult}
             isUpdate={isUpdate}
             handleChangeToCreate={handleChangeToCreate}
             handleChangeToConsult={handleChangeToConsult}
             handleChangeToUpdate={handleChangeToUpdate}
             handleCancelUpdate={handleCancelUpdate}
             authInfo={authInfo}
           >
             {(formProps) => (
               <QCXTarifaForm
                   isCreate={isCreate}
                   initialValues={model}
                   handleSubmit={handleSubmit}
                   handleChangeModel={handleDispatchSetModel}
                   authInfo={authInfo}
                   requiredRoles={['grupo-despesa-receita']}
                   {...formProps}
               />
             )}
           </QCXRegistrationFormPageTemplate>
    )


}